import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { useEffect } from 'react';
import { useAuthStore, useThemeStore } from '@/store';
import TradeOffersPage from '@/pages/TradeOffersPage';
import ForumHomePage from '@/pages/forum/ForumHomePage';
import ForumCategoryPage from '@/pages/forum/ForumCategoryPage';
import ForumThreadPage from '@/pages/forum/ForumThreadPage';
import NewThreadPage from '@/pages/forum/NewThreadPage';

// Layout
import MainLayout from '@/components/layout/MainLayout';

// Pages
import HomePage from '@/pages/HomePage';
import ExplorePage from '@/pages/ExplorePage';
import CardDetailPage from '@/pages/CardDetailPage';
import MarketplacePage from '@/pages/MarketplacePage';
import ListingDetailPage from '@/pages/ListingDetailPage';
import LoginPage from '@/pages/auth/LoginPage';
import RegisterPage from '@/pages/auth/RegisterPage';
import DashboardPage from '@/pages/dashboard/DashboardPage';
import ProfilePage from '@/pages/ProfilePage';
import UserListingsPage from '@/pages/UserListingsPage';
import CollectionPage from '@/pages/CollectionPage';
import SetDetailPage from '@/pages/SetDetailPage';
import AddCardsPage from '@/pages/AddCardsPage';
import WishlistPage from '@/pages/WishlistPage';
import CreateListingPage from '@/pages/CreateListingPage';
import NotFoundPage from '@/pages/NotFoundPage';
import MessagesPage from '@/pages/MessagesPage';
// import ConversationPage from './pages/Conversationpage';
import ConversationPage from '@/pages/ConversationPage';
import NotificationsPage from './pages/NotificationsPage';
import SettingsPage from '@/pages/SettingsPage';
import VerifyEmailPage from '@/pages/VerifyEmailPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import ChangePasswordPage from '@/pages/ChangePasswordPage';
import DeleteAccountPage from '@/pages/DeleteAccountPage';
import CheckoutSuccessPage from '@/pages/CheckoutSuccessPage';
import CheckoutCancelPage from '@/pages/CheckoutCancelPage';
import DeckBuilderPage from '@/pages/DeckBuilderPage';
import MissingCardsPage from '@/pages/MissingCardsPage';
import TrophyCasePage from '@/pages/TrophyCasePage';
import LeaderboardPage from '@/pages/LeaderboardPage';
import ProgressionProvider from '@/components/progression/ProgressionProvider';
import CookieConsentBanner from '@/components/CookieConsentBanner';
import PrivacyPolicyPage from '@/pages/legal/PrivacyPolicyPage';
import TermsOfServicePage from '@/pages/legal/TermsOfServicePage';
import CommunityGuidelinesPage from '@/pages/legal/CommunityGuidelinesPage';
import PlatformGuidePage from '@/pages/PlatformGuidePage';
import AboutPage from '@/pages/AboutPage';
import EditListingPage from '@/pages/EditListingPage';
import CartPage from '@/pages/CartPage';
import ContactPage from '@/pages/ContactPage';
import AdminModerationPage from '@/pages/admin/AdminModerationPage';
import AdminLayout from '@/components/admin/AdminLayout';
import AdminDashboardPage from '@/pages/admin/AdminDashboardPage';
import AdminUsersPage from '@/pages/admin/AdminUsersPage';
import AdminListingsPage from '@/pages/admin/AdminListingsPage';
import AdminReportsPage from '@/pages/admin/AdminReportsPage';
import AdminAnnouncementsPage from '@/pages/admin/AdminAnnouncementsPage';
import AdminAnalyticsPage from '@/pages/admin/AdminAnalyticsPage';
import AdminAuditLogPage from '@/pages/admin/AdminAuditLogPage';


// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
}

// Public Only Route (redirect if authenticated)
function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
}

function App() {
  const isDark = useThemeStore((state) => state.isDark);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
  }, [isDark]);

  return (
    <QueryClientProvider client={queryClient}>
      <ProgressionProvider>
      <Router>
        <Routes>
          {/* Public routes */}
          <Route element={<MainLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/explore" element={<ExplorePage />} />
            <Route path="/cards/:id" element={<CardDetailPage />} />
            <Route path="/marketplace" element={<MarketplacePage />} />
            <Route path="/listings/:id" element={<ListingDetailPage />} />
            <Route path="/listings/:id/edit" element={<EditListingPage />} />
            <Route path="/users/:id" element={<ProfilePage />} />
            <Route path="/profile/:id" element={<ProfilePage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/profile/:id/listings" element={<UserListingsPage />} />
            <Route path="/profile/listings" element={<UserListingsPage />} />
            <Route path="/forum" element={<ForumHomePage />} />
            <Route path="/forum/threads/:id" element={<ForumThreadPage />} />
            <Route path="/forum/:slug" element={<ForumCategoryPage />} />
          </Route>

          {/* Email action routes (public) */}
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/change-password" element={<ChangePasswordPage />} />
          <Route path="/delete-account" element={<DeleteAccountPage />} />

          {/* Checkout result routes (public — Stripe redirects here) */}
          <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
          <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />

          {/* Legal & contact (public, with nav) */}
          <Route element={<MainLayout />}>
            <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/legal/terms" element={<TermsOfServicePage />} />
            <Route path="/legal/community-guidelines" element={<CommunityGuidelinesPage />} />
            <Route path="/guide" element={<PlatformGuidePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/admin/moderation" element={<AdminModerationPage />} />
          </Route>

          {/* Admin panel */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboardPage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="listings" element={<AdminListingsPage />} />
            <Route path="reports" element={<AdminReportsPage />} />
            <Route path="announcements" element={<AdminAnnouncementsPage />} />
            <Route path="analytics" element={<AdminAnalyticsPage />} />
            <Route path="audit-log" element={<AdminAuditLogPage />} />
          </Route>

          {/* Auth routes (public only) */}
          <Route
            path="/login"
            element={
              <PublicOnlyRoute>
                <LoginPage />
              </PublicOnlyRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicOnlyRoute>
                <RegisterPage />
              </PublicOnlyRoute>
            }
          />

          {/* Protected routes */}
          <Route element={<MainLayout />}>
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/collection"
              element={
                <ProtectedRoute>
                  <CollectionPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/collection/sets/detail"
              element={
                <ProtectedRoute>
                  <SetDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/collection/add"
              element={
                <ProtectedRoute>
                  <AddCardsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wishlist"
              element={
                <ProtectedRoute>
                  <WishlistPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/listings/new"
              element={
                <ProtectedRoute>
                  <CreateListingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/listings/create"
              element={
                <ProtectedRoute>
                  <CreateListingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/messages"
              element={
                <ProtectedRoute>
                  <MessagesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/messages/:id"
              element={
                <ProtectedRoute>
                  <ConversationPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/forum/new"
              element={
                <ProtectedRoute>
                  <NewThreadPage />
                </ProtectedRoute>
              }
            />
          </Route>

          <Route path="/trade-offers" element={<ProtectedRoute><TradeOffersPage /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
          <Route path="/cart" element={<ProtectedRoute><CartPage /></ProtectedRoute>} />

          {/* Deck builder — full-screen, lives under /collection */}
          <Route path="/collection/decks/:deckId" element={<ProtectedRoute><DeckBuilderPage /></ProtectedRoute>} />
          <Route path="/collection/decks/:deckId/missing" element={<ProtectedRoute><MissingCardsPage /></ProtectedRoute>} />

          {/* Progression */}
          <Route path="/trophies" element={<ProtectedRoute><TrophyCasePage /></ProtectedRoute>} />
          <Route path="/trophies/:id" element={<TrophyCasePage />} />
          <Route path="/leaderboards" element={<LeaderboardPage />} />

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>

        <CookieConsentBanner />
      </Router>

      {/* Toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      </ProgressionProvider>
    </QueryClientProvider>
  );
}

export default App;
